import moment from 'moment';
import { Socket, getEnvVar } from '@shoflo/polymer';
import { sendToDatadog, StudioLogger } from '@studio/logger';
import * as Events from '@shoflo/studio-redux/constants/SocketEvents';
import {
  setAttendeeCameraStatus,
  removeSandboxMeetingAttendee,
  syncBrandingSettings,
  getSources
} from '@shoflo/studio-redux/actions';
import {
  KNOCK_ANSWERED,
  LIMITED_BROADCAST_UPDATED,
  BROADCAST_JOIN_SYNCED_SERVER_TIME,
  REAL_TIME
} from '../eventConstants';
import {
  SOURCE_CREATED,
  SOURCE_DELETED,
  SOURCE_TOGGLED,
  AGORA_CAMERA_STATUS_CHANGED,
  AGORA_ATTENDEE_MUTE_UNMUTE_CHANGED,
  AGORA_SCREEN_SHARE_STOPPED
} from '@shoflo/studio-redux/constants/SocketEvents';
import {
  joinMeeting,
  setMixerAttendee,
  setAttendeeRoster,
  muteAudio,
  setMediaPlayerSettings,
  toggleScreenShareActive,
  toggleVideoActive,
  setDirectorControls,
  clearDirectorRequest,
  setDirectorRequestMessage,
  leaveMeeting,
  setPresenterDismissed,
  setContentShareAudioProfile,
  muteMeetingAudio as muteMeetingAudioAction,
  setBroadcastSettings,
  knock as knockAction,
  setKnockFailed,
  setServerTimeDelta,
  renewMeeting,
  cancelPendingRequest,
  setAttendeeMuteStatus,
  removeScreenShareFromProgram,
  setScreenShareActive,
  setProgram
} from 'actions/Broadcast';

import {
  programChanged,
  sessionStateChanged,
  limitedBroadcastUpdated
} from './handlers';
import { handleMuteStatus } from 'actions/SocketHandler';
import {
  MUTE,
  CAMERA,
  SCREEN_SHARE,
  DIRECTOR_REQUEST_DENIED,
  DIRECTOR_REQUEST_ACCEPTED
} from 'constants/DirectorControls';
import { BROADCAST_KNOCK_REJECTED } from 'actions/Types';
import {
  PRESENTER_BIT_RATE_CHANGED,
  MUTE_MEETING_AUDIO_CHANGED,
  TOGGLED_ADD_SELF_LIVE,
  ATTENDEE_DETAILS_CHANGED,
  ATTENDEE_SCREEN_SHARE_CANCELLED,
  MULTIPLE_MUTE_CHANGED,
  MULTIPLE_CAMERA_STATUSES_CHANGED,
  KNOCK_FAILED,
  TOGGLED_LAYOUT_ANIMATION,
  RENEWED_MEETING,
  SYNCED_BRANDING_SETTINGS
} from 'constants/SocketConstants';
import { knockTypeToControl } from 'constants/Knock';
import setStreamStatus from 'actions/Broadcast/setStreamStatus';
import { clearNotification, showNotification } from 'actions';
import {
  MAX_CAMERA_LIMIT,
  REALTIME_CONNECTION_TIMEOUT_VALUE,
  SCREEN_SHARE_REQUEST_STATUS
} from '../../constants';
import checkRealtimeSocketConnection from 'actions/CheckRealtimeSocketConnection';
import { clearModal } from 'actions';
import { SCREENSHARE_MODAL } from 'constants/ModalTypes';
import { shouldJoinWithoutKnock } from '../../utils/broadcastPresenterHelper';
import syncScreenShare from 'utils/syncScreenShare/syncScreenShare';
import agoraKnockAnswered from './handlers/agoraKnockAnswered/agoraKnockAnswered';
import { VENDOR_AGORA } from '@studio/constants';
import { shouldHaveFeature } from '@studio/feature.flags';
import {
  agoraCameraStatusChanged,
  agoraAttendeeMuteUnmuteChanged
} from '@shoflo/studio-redux/SocketDispatcher';
import { setScreenShareRequest } from '../../actions/Broadcast/setScreenShareRequest';
import { processIncomingRealtimeMessage } from '../socket-handlers/processIncomingRealtimeMessage';

const LOG = new StudioLogger('SocketDispatcher');

/**
 * This class connects the redux store to the socket connection,
 * and manages joining / leaving event socket.io rooms as the event changes
 *
 */
const SocketDispatcher = function () {
  this.connect = function () {
    // Create a new socket object
    this._socket = new Socket(getEnvVar('REACT_APP_SF_REALTIME_ENDPOINT'));
    return this._socket;
  };

  this.bindBroadcastEventsToSocket = function (store) {
    const { broadcast } = store.getState();
    const shouldJoinWithoutKnockEnabled = shouldJoinWithoutKnock(broadcast);

    this._socket.on('cameraStatusChanged', ({ attendeeId, isCameraOn }) => {
      const {
        meeting: { attendeeResponse }
      } = store.getState();

      if (
        !attendeeResponse ||
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        // the change was initiated by the director or by this presenter. We
        // need to update this presenter's camera status on chime and the UI
        // accordingly if it is from director.
        this._handleCameraStatusChanged(isCameraOn);
      }

      store.dispatch(
        setAttendeeCameraStatus(attendeeId, isCameraOn, setProgram)
      );
    });

    this._socket.on('attendeeDismissed', ({ attendeeId }) => {
      const {
        meeting: { attendeeResponse },
        attendeePresence
      } = store.getState();

      //If the local attendee matches the attendee who was dismissed, fire the action
      if (
        attendeeResponse &&
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        if (attendeePresence.includes(attendeeId)) {
          store.dispatch(setPresenterDismissed(true));
        }
        store.dispatch(leaveMeeting());
      }
    });

    this._socket.on('screenShareStopped', ({ attendeeId }) => {
      LOG.info('screenShareStopped for attendeeId:', attendeeId);
      const {
        meeting: { attendeeResponse },
        isScreenShareActive,
        modal
      } = store.getState();

      //If the local attendee matches the attendee who's Screenshare we want to stop, we fire this action
      if (
        attendeeResponse &&
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        const isModalOpen = modal?.type === SCREENSHARE_MODAL;
        store.dispatch(cancelPendingRequest({ requestType: SCREEN_SHARE }));
        if (isModalOpen) {
          store.dispatch(clearModal(SCREENSHARE_MODAL));
        }

        if (isScreenShareActive) {
          store.dispatch(toggleScreenShareActive());
        }
      }
    });

    /**
     * When presenter cancels screen share from browser picker or an error occurs,
     * this resets the screen share request state.
     */
    this._socket.on(ATTENDEE_SCREEN_SHARE_CANCELLED, attendeeId => {
      const {
        meeting: { attendeeResponse }
      } = store.getState();

      if (
        attendeeResponse &&
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        store.dispatch(cancelPendingRequest({ requestType: SCREEN_SHARE }));
      }
    });

    this._socket.on('muteChanged', ({ attendeeId, isMuted }) => {
      const {
        meeting: { attendeeResponse }
      } = store.getState();

      //If the local attendee matches the attendee who's mute status changed, fire the action
      if (
        attendeeResponse &&
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        this._handleMuteStatus(isMuted);
      }
    });

    this._socket.on(ATTENDEE_DETAILS_CHANGED, async payload => {
      const { title, description, attendeeId, presenterPhoto = '' } = payload;
      const { user } = store.getState();
      // Check if is same attendee
      if (!this.isSameAttendee(attendeeId)) return;

      store.dispatch(
        knockAction({
          presenterName: title,
          presenterDescription: description,
          isTransient: false,
          presenterPhoto: user?.photo || presenterPhoto
        })
      );
    });

    this._socket.on(
      MUTE_MEETING_AUDIO_CHANGED,
      async ({ muteMeetingAudio }) => {
        if (shouldJoinWithoutKnockEnabled) {
          store.dispatch(
            setBroadcastSettings({
              muteMeetingAudio
            })
          );
        } else {
          store.dispatch(muteMeetingAudioAction(muteMeetingAudio));
        }
      }
    );

    // When Director Request is answered
    this._socket.on('directorRequestAnswered', payload => {
      this.handleDirectorRequestAnswered(payload);
    });

    this._socket.on(REAL_TIME, async payload => {
      processIncomingRealtimeMessage.call({ store, payload });
    });

    // When the presenter's attendeeId is among the muteStatuses that changed,
    // update attendee mute status
    this._socket.on(MULTIPLE_MUTE_CHANGED, ({ muteStatuses }) => {
      const {
        meeting: { attendeeResponse }
      } = store.getState();

      const attendeeId =
        attendeeResponse && attendeeResponse.Attendee.AttendeeId;
      const hasAttendeeId = typeof muteStatuses[attendeeId] === 'boolean';

      if (!hasAttendeeId) {
        return;
      }

      const isMuted = muteStatuses[attendeeId];
      this._handleMuteStatus(isMuted);
    });

    this._socket.on(
      MULTIPLE_CAMERA_STATUSES_CHANGED,
      ({ attendeeCameraStatuses }) => {
        const {
          meeting: { attendeeResponse }
        } = store.getState();

        const attendeeId =
          attendeeResponse && attendeeResponse.Attendee.AttendeeId;

        const isCurrentAttendee =
          typeof attendeeCameraStatuses[attendeeId] === 'boolean';

        if (!isCurrentAttendee) return;

        const isCameraOn = attendeeCameraStatuses[attendeeId];
        this._handleCameraStatusChanged(isCameraOn, isCurrentAttendee);
      }
    );

    this._socket.on(TOGGLED_ADD_SELF_LIVE, async ({ addSelfLive }) => {
      store.dispatch(setBroadcastSettings({ addSelfLive }));
    });

    this._socket.on(TOGGLED_LAYOUT_ANIMATION, async ({ layoutAnimations }) => {
      store.dispatch(setBroadcastSettings({ layoutAnimations }));
    });

    this._socket.on('videoQualityChanged', ({ attendeeId, ...settings }) => {
      const {
        meeting: { meetingSession, attendeeResponse }
      } = store.getState();

      //If the local attendee matches the attendee who's mute status changed, fire the action
      if (
        attendeeResponse &&
        attendeeResponse.Attendee.AttendeeId === attendeeId
      ) {
        LOG.info('video quality changed', attendeeId, settings);

        this._store.dispatch(toggleVideoActive());
        meetingSession.audioVideo.chooseVideoInputQuality(
          settings.width,
          settings.height,
          parseInt(settings.frameRate)
        );
        meetingSession.audioVideo.setVideoMaxBandwidthKbps(
          parseInt(settings.bitRate)
        );
        this._store.dispatch(toggleVideoActive());
      }
    });

    // When director changes audio bitrate for an attendee
    this._socket.on(
      PRESENTER_BIT_RATE_CHANGED,
      async ({ attendeeId, contentShareAudioProfile }) => {
        const {
          meeting: { attendeeResponse }
        } = store.getState();
        if (
          attendeeResponse &&
          attendeeResponse.Attendee.AttendeeId === attendeeId
        ) {
          if (contentShareAudioProfile) {
            store.dispatch(setContentShareAudioProfile());
          }
        }
      }
    );
  };

  /**
   * Function to bind the store to function context
   * @param store
   * @returns {*}
   */
  this.bindToStore = function (store) {
    const boundProgramChangedHandler = programChanged.bind(null, store);
    const shouldJoinWithoutKnockEnabled = shouldJoinWithoutKnock(
      store.getState().broadcast
    );
    this._store = store;
    /** Util dispatcher methods */
    // returns boolean if attendeeId is same sending director updates
    this.isSameAttendee = attendeeId => {
      const { meeting } = this._store.getState();
      const AttendeeId = meeting?.attendeeResponse?.Attendee?.AttendeeId;
      return AttendeeId === attendeeId;
    };
    // handles a set of actions to accept a director request
    this.handleDirectorRequestAnswered = answeredRequest => {
      const {
        accepted,
        rejected,
        status,
        attendeeId,
        requestType,
        type,
        sourceId,
        _id: requestId
      } = answeredRequest;

      const isAccepted = status === DIRECTOR_REQUEST_ACCEPTED || accepted;
      const isRejected = status === DIRECTOR_REQUEST_DENIED || rejected;
      const directorControlKey =
        (type && knockTypeToControl[type]) || requestType;
      const isShareScreen = directorControlKey === SCREEN_SHARE;

      // Check if is same attendee
      if (
        !(this.isSameAttendee(attendeeId) || this.isPresenterRequest(requestId))
      )
        return;

      if (isAccepted) {
        // If director request is accepted or approved
        this._store.dispatch(
          setDirectorControls({
            key: directorControlKey,
            value: true
          })
        );

        /**
         * After host approves screen share request,
         * we need this state to ensure popover
         * modal remains open until speaker choose
         * screen to share or cancel request after
         * which we reset director request to null
         */
        this._store.dispatch(
          setDirectorRequestMessage({
            key: directorControlKey,
            value: DIRECTOR_REQUEST_ACCEPTED
          })
        );

        if (isShareScreen) {
          this._store.dispatch(toggleScreenShareActive());
          this._store.dispatch(
            setScreenShareRequest({
              status: SCREEN_SHARE_REQUEST_STATUS.ACCEPTED,
              sourceId
            })
          );
        }
      }
      // If director request is rejected or denied
      if (isRejected) {
        this._store.dispatch(
          setDirectorRequestMessage({
            key: directorControlKey,
            value: DIRECTOR_REQUEST_DENIED
          })
        );

        this._store.dispatch(
          setScreenShareRequest({
            status: SCREEN_SHARE_REQUEST_STATUS.DENIED
          })
        );

        this._store.dispatch(
          clearDirectorRequest({
            requestId,
            requestType: requestType || directorControlKey
          })
        );
      }
    };

    this._handleMuteStatus = isMuted => {
      store.dispatch(handleMuteStatus(isMuted));
    };

    this._handleCameraStatusChanged = (
      isCameraOn,
      isCurrentAttendee = true
    ) => {
      const {
        attendeeCameraStatus,
        isVideoActive,
        videoInputDevice,
        meeting: { attendeeResponse }
      } = store.getState();

      const liveCameras = Object.values(attendeeCameraStatus).filter(
        item => item === true
      ).length;

      const attendeeId = attendeeResponse?.Attendee?.AttendeeId;
      if (videoInputDevice && isCameraOn !== attendeeCameraStatus[attendeeId]) {
        this._store.dispatch(toggleVideoActive(false, 'host'));
      }

      // We want to override the current attendee's camera when
      // their camera is off and the max camera limit has been reached
      if (
        !isCurrentAttendee &&
        !isVideoActive &&
        liveCameras >= MAX_CAMERA_LIMIT
      ) {
        this._store.dispatch(
          setDirectorControls({
            key: CAMERA,
            value: false
          })
        );
      } else {
        this._store.dispatch(
          setDirectorControls({
            key: CAMERA,
            value: isCameraOn
          })
        );
      }

      this._store.dispatch(
        setDirectorRequestMessage({
          key: CAMERA,
          value: null
        })
      );
    };

    this.isPresenterRequest = requestId => {
      const { directorRequest } = this._store.getState();

      return !!directorRequest.find(req => req._id === requestId);
    };

    this.isSameSocketId = socketId => {
      return this._socket?._socket?.id === socketId;
    };

    this.isSameKnock = knockId => {
      const { knock } = this._store.getState();
      return knock?._id === knockId;
    };

    this.canPresenterJoin = data => {
      const { knockId, socketId } = data;

      return this.isSameSocketId(socketId) || this.isSameKnock(knockId);
    };

    socketReconnected(this._socket, this._store);
    socketDisconnected(this._socket, this._store);
    socketConnected(this._socket, this._store);

    // Bind broadcast events once during initialization
    this.bindBroadcastEventsToSocket(store);

    //When the "director" answers a knock, i.e. they are allowed into the meeting, join the meeting
    this._socket.on(KNOCK_ANSWERED, data => {
      const { program, streamRecordStatus } = data;

      const { meeting } = this._store.getState();

      const presenterAttendeeId =
        meeting?.attendeeResponse?.Attendee?.AttendeeId;
      /**
       * Check if presenter can join meeting
       * If Presenter is already in meeting or meeting has started, no need to join the meeting again
       * meeting.started is set when join begins (BROADCAST_JOIN_MEETING for Chime or BROADCAST_CREATE_MEETING for Agora),
       * preventing duplicate join attempts from multiple KNOCK_ANSWERED events (e.g., multiple approve clicks or multiple hosts approving)
       */
      if (
        !this.canPresenterJoin(data) ||
        presenterAttendeeId ||
        meeting?.started
      )
        return;
      const useAgora =
        shouldHaveFeature('studio.meetingVendor') === VENDOR_AGORA;
      if (useAgora) {
        return agoraKnockAnswered(store, data, boundProgramChangedHandler);
      }

      // The sandboxMeetingAttendee should be removed when the presenter is allowed into the meeting
      store.dispatch(removeSandboxMeetingAttendee());

      const { program: currentProgram } = store.getState();

      if (!currentProgram) {
        boundProgramChangedHandler({ program });
      }

      store.dispatch(joinMeeting(data));

      if (!shouldJoinWithoutKnockEnabled) {
        store.dispatch(muteMeetingAudioAction(data.muteMeetingAudio));
      } else {
        data.broadcastSettings = {
          ...data.broadcastSettings,
          muteMeetingAudio: data.muteMeetingAudio
        };
      }

      store.dispatch(setBroadcastSettings(data.broadcastSettings));

      let { canControlMic = false } = data.knockAVStatus || {};

      if ('boolean' !== typeof canControlMic) canControlMic = false;

      if (canControlMic) {
        // Enable mute toggle button
        store.dispatch(
          setDirectorControls({
            key: MUTE,
            value: true
          })
        );
      } else {
        store.dispatch(muteAudio());
      }

      store.dispatch(setStreamStatus(streamRecordStatus));
    });

    //When the "director" rejects a knock, i.e. they are not allowed into the meeting
    this._socket.on('knockRejected', ({ socketId, knockId }) => {
      if (this.isSameSocketId(socketId) || this.isSameKnock(knockId)) {
        store.dispatch({ type: BROADCAST_KNOCK_REJECTED });
      }
    });

    //When the "mixer" user agent joins the chime session, set the state
    this._socket.on('broadcastMixerJoined', ({ attendeeId }) => {
      LOG.info('broadcastMixerJoined w/ attendeeId:', attendeeId);

      store.dispatch(setMixerAttendee(attendeeId));
    });

    //When the roster for the meeting changes, update state
    this._socket.on('attendeeRosterChanged', ({ attendeeRoster }) => {
      LOG.info('attendeeRosterChanged', attendeeRoster);

      store.dispatch(setAttendeeRoster(attendeeRoster));
    });

    this._socket.on('broadcastProgramChanged', boundProgramChangedHandler);
    this._socket.on('broadcastProgramSynced', boundProgramChangedHandler);

    // When the mediaPlayer in program changes, update state
    this._socket.on('mediaPlayerChanged', ({ mediaPlayer }) => {
      store.dispatch(setMediaPlayerSettings(mediaPlayer));
    });

    this._socket.on(SOURCE_CREATED, () => {
      store.dispatch(getSources());
    });

    this._socket.on(SOURCE_DELETED, () => {
      store.dispatch(getSources());
    });

    this._socket.on(
      AGORA_CAMERA_STATUS_CHANGED,
      async ({ attendeeId, isCameraOn, notifyingAttendee }) => {
        agoraCameraStatusChanged(store, {
          attendeeId,
          isCameraOn,
          notifyingAttendee
        });
      }
    );

    this._socket.on(
      AGORA_ATTENDEE_MUTE_UNMUTE_CHANGED,
      async ({ attendeeId, isMuted, notifyingAttendee }) => {
        agoraAttendeeMuteUnmuteChanged(store, {
          attendeeId,
          isMuted,
          setAttendeeMuteStatus,
          notifyingAttendee
        });
      }
    );

    this._socket.on(AGORA_SCREEN_SHARE_STOPPED, async ({ attendeeId }) => {
      const {
        meeting: { screenShare, attendeeId: localAttendeeId }
      } = store.getState();

      const localScreenShareId = `${localAttendeeId}#content`;

      // If the local attendee's screen share was stopped remotely
      if (localScreenShareId === attendeeId) {
        const { client, videoTrack, audioTrack } = screenShare || {};

        // Only call stopScreenShare if client is still connected/joined
        // If user stopped via browser, the client will already be DISCONNECTED
        if (client && videoTrack && client.connectionState === 'CONNECTED') {
          const { stopScreenShare } = await import(
            '@cvent/studio-agora/screenShare/screenShareHelper'
          );
          await stopScreenShare(client, videoTrack, audioTrack);
        }

        store.dispatch(removeScreenShareFromProgram(attendeeId));
        store.dispatch(setScreenShareActive(false));
      }
    });

    this._socket.on(SOURCE_TOGGLED, () => {
      store.dispatch(getSources());
    });

    this._socket.on(
      BROADCAST_JOIN_SYNCED_SERVER_TIME,
      async ({ serverTime }) => {
        const presenterTime = moment().utc().unix();
        const now = moment.unix(presenterTime);
        const then = moment.unix(serverTime);
        const serverTimeDelta = now.diff(then, 'seconds'); // Get seconds difference
        store.dispatch(setServerTimeDelta(serverTimeDelta));
      }
    );

    //When the "director" sends a knock failed message
    this._socket.on(KNOCK_FAILED, payload => {
      const { socketId, knockId } = payload;
      if (this.isSameSocketId(socketId) || this.isSameKnock(knockId)) {
        store.dispatch(setKnockFailed(true));
      }
    });

    this._socket.on(RENEWED_MEETING, async payload => {
      store.dispatch(renewMeeting(payload));
    });

    this._socket.on(
      Events.SESSION_STATE_CHANGED,
      sessionStateChanged.bind(null, this._store)
    );

    this._socket.on(
      LIMITED_BROADCAST_UPDATED,
      limitedBroadcastUpdated.bind(null, this._store)
    );

    this._socket.on(SYNCED_BRANDING_SETTINGS, async ({ brandingSettings }) => {
      store.dispatch(syncBrandingSettings(brandingSettings));
    });

    return this._socket;
  };
};

/**
 *
 * @param {*} socket
 * @param {*} store
 */

const socketReconnected = (socket, store) => {
  socket._socket.io.on('reconnect', () => {
    LOG.info('socket reconnect event fired!');
    store.dispatch(clearNotification());
    store.dispatch(
      showNotification('Realtime connection restored', {
        timeout: 2000,
        type: 'success'
      })
    );

    syncScreenShare(store.dispatch, store.getState);

    const {
      meeting: { attendeeResponse }
    } = store.getState();
    const payload = {
      attendeeId: attendeeResponse?.Attendee?.AttendeeId ?? 'unavailable'
    };
    sendToDatadog(
      'socket-dispatcher',
      payload,
      'Realtime connection restored',
      true
    );
  });
  store.dispatch(checkRealtimeSocketConnection());
};

const socketDisconnected = (socket, store) => {
  socket.on('disconnect', () => {
    store.dispatch(clearNotification());
    if (!window.realtimeConnectionLostTimeout) {
      window.realtimeConnectionLostTimeout = setTimeout(() => {
        store.dispatch(
          showNotification(
            'Realtime connection lost. Attempting to re-connect...',
            {
              timeout: 1000 * 60 * 10,
              type: 'warning'
            }
          )
        );
      }, REALTIME_CONNECTION_TIMEOUT_VALUE);
    }

    const {
      meeting: { attendeeResponse }
    } = store.getState();
    const payload = {
      attendeeId: attendeeResponse?.Attendee?.AttendeeId ?? 'unavailable'
    };
    sendToDatadog(
      'socket-dispatcher',
      payload,
      'Realtime connection lost',
      true
    );
  });

  store.dispatch(checkRealtimeSocketConnection());
};

const socketConnected = (socket, store) => {
  socket.on('connect', () => {
    window.realtimeConnectionLostTimeout = null;
    store.dispatch(checkRealtimeSocketConnection());
  });

  const {
    meeting: { attendeeResponse }
  } = store.getState();
  const payload = {
    attendeeId: attendeeResponse?.Attendee?.AttendeeId ?? 'unavailable'
  };
  sendToDatadog('socket-dispatcher', payload, 'Realtime socket connected');
};

export default SocketDispatcher;
